import React, { useState, useEffect } from 'react';
import { notificationService } from '../services/notificationService';
import { useNotification } from '../components/NotificationContext';
import { 
  CheckCircleIcon, 
  XMarkIcon, 
  BellIcon, 
  ArrowPathIcon, 
  ChevronDownIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import useTranslation from '../services/translationService';

const NotificationItem = ({ notification, onMarkAsRead, isUnread }) => {
  const { t } = useTranslation();
  const { content, sender, createdAt, id } = notification;
  
  // Determine notification type based on content
  const getNotificationType = () => {
    const contentLower = content.toLowerCase();
    if (contentLower.includes('error') || contentLower.includes('failed')) {
      return 'error';
    } else if (contentLower.includes('warning') || contentLower.includes('caution')) {
      return 'warning';
    } else {
      return 'info';
    }
  };
  
  const type = getNotificationType();
  
  // Format the time as "X minutes/hours/days ago"
  const formattedTime = formatDistanceToNow(new Date(createdAt), { addSuffix: true });
  
  const getTypeStyles = () => {
    switch (type) {
      case 'info':
        return 'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200 border-blue-100 dark:border-blue-800/30';
      case 'success':
        return 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200 border-green-100 dark:border-green-800/30';
      case 'warning':
        return 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200 border-yellow-100 dark:border-yellow-800/30';
      case 'error':
        return 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200 border-red-100 dark:border-red-800/30';
      default:
        return 'bg-gray-50 text-gray-800 dark:bg-gray-800/50 dark:text-gray-200 border-gray-100 dark:border-gray-700';
    }
  };

  return (
    <div className={`p-4 rounded-lg mb-4 border ${getTypeStyles()} transition-all duration-200 hover:shadow-md ${isUnread ? 'border-l-4 border-l-blue-500' : ''}`}>
      <div className="flex justify-between items-start">
        <div className="flex gap-3">
          <div className="flex-shrink-0 mt-1">
            <UserIcon className="w-6 h-6 text-gray-500" />
          </div>
          <div className="flex-grow">
            <div className="font-semibold text-sm flex items-center">
              {sender || t('notifications.system', 'System')}
              {isUnread && (
                <span className="ml-2 inline-block w-2 h-2 rounded-full bg-blue-500"></span>
              )}
            </div>
            <p className="text-sm mt-1">{content}</p>
            <div className="flex items-center mt-2">
              <span className="text-xs opacity-75">{formattedTime}</span>
            </div>
          </div>
        </div>
        {isUnread && (
          <button 
            onClick={() => onMarkAsRead(id)}
            className="ml-2 px-2 py-1 rounded-md text-xs font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center space-x-1"
          >
            <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
            <span>{t('notifications.markRead', 'Mark read')}</span>
          </button>
        )}
      </div>
    </div>
  );
};

const Notifications = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const { showNotification, unreadMessages, markAsRead, markAllAsRead } = useNotification();

  // Helper function to sort notifications by createdAt (newest first)
  const sortNotifications = (notifs) => {
    return [...notifs].sort((a, b) => {
      // Ensure we have valid dates
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      
      // Handle invalid dates
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      
      return dateB - dateA; // Newest first
    });
  };

  // Helper function to merge and deduplicate notifications
  const mergeNotifications = (existing, newNotifs) => {
    const allNotifs = [...existing];
    const existingIds = new Set(existing.map(n => n.id));
    
    // Add only non-duplicate notifications
    newNotifs.forEach(notif => {
      if (!existingIds.has(notif.id)) {
        allNotifs.push(notif);
      }
    });
    
    // Sort all notifications by date
    const sorted = sortNotifications(allNotifs);
    
    // Debug log to verify sorting
    if (sorted.length > 0) {
      console.log('Notifications sorted - Latest:', sorted[0].createdAt, 'Oldest:', sorted[sorted.length - 1].createdAt);
    }
    
    return sorted;
  };

  // When viewing the notifications page, add unread messages to the top
  useEffect(() => {
    // Merge unread messages with existing notifications
    if (unreadMessages.length > 0) {
      setNotifications(prev => mergeNotifications(prev, unreadMessages));
    }
  }, [unreadMessages]);

  // When viewing the notifications page, mark all as read after a delay
  useEffect(() => {
    if (unreadMessages.length > 0) {
      // Add a longer delay before marking as read
      const timeout = setTimeout(() => {
        markAllAsRead();
      }, 5000); // Mark all as read after 5 seconds of viewing the page
      
      return () => clearTimeout(timeout);
    }
  }, [unreadMessages.length, markAllAsRead]);

  // Register for real-time notifications
  useEffect(() => {
    console.log('Setting up notification listener in Notifications page');
    
    const cleanup = notificationService.onNotification((message) => {
      console.log('Received new notification in Notifications page:', message);
      
      // Merge new message with existing notifications
      setNotifications(prev => mergeNotifications(prev, [message]));
    });
    
    return cleanup;
  }, []);

  // Fetch notification history on component mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async (token) => {
    try {
      setLoading(true);
      const { messages, nextToken: newToken } = await notificationService.getMessageHistory({
        limit: 10,
        nextToken: token
      });
      
      if (token) {
        // Merge paginated messages with existing notifications
        setNotifications(prev => mergeNotifications(prev, messages));
      } else {
        // For initial load, sort the messages to ensure proper order
        setNotifications(sortNotifications(messages));
      }
      
      setNextToken(newToken);
      setHasMore(!!newToken);
      setError(null);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(t('notifications.loadError', 'Failed to load notifications'));
      showNotification(t('notifications.loadError', 'Failed to load notifications'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    // Reset state and fetch from beginning
    setNotifications([]);
    setNextToken(null);
    setHasMore(false);
    fetchNotifications();
  };

  const handleLoadMore = () => {
    if (nextToken) {
      fetchNotifications(nextToken);
    }
  };

  const handleMarkAllAsRead = () => {
    // Mark all as read in the context
    markAllAsRead();
    // Don't remove notifications from the list, just mark them as read
  };

  const handleMarkAsRead = (id) => {
    // Mark as read in the context
    markAsRead(id);
    // Update UI but don't remove
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true } 
          : notification
      )
    );
  };

  // Check if a notification is unread
  const isUnread = (notification) => {
    return unreadMessages.some(m => m.id === notification.id);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
          <div className="flex items-center">
            <BellIcon className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-300" />
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center">
                {t('navigation.notifications', 'Notifications')}
                {unreadMessages.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 rounded-full text-xs font-medium">
                    {unreadMessages.length} {t('notifications.unread', 'unread')}
                  </span>
                )}
              </h1>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t('notifications.sortedNewest', 'Sorted by newest first')}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={handleRefresh} 
              className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
              title={t('common.refresh', 'Refresh')}
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {unreadMessages.length > 0 && (
              <button 
                onClick={handleMarkAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              >
                {t('notifications.markAllRead', 'Mark all as read')}
              </button>
            )}
          </div>
        </div>
        
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="flex justify-center items-center py-10">
              <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500 dark:text-gray-400">{t('notifications.loading', 'Loading notifications...')}</span>
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-500 dark:text-red-400">
              <p>{error}</p>
              <button 
                onClick={handleRefresh}
                className="mt-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-md text-sm"
              >
                {t('common.tryAgain', 'Try again')}
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-16">
              <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                <BellIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-gray-500 dark:text-gray-400">{t('notifications.noNotifications', 'No notifications yet')}</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                {t('notifications.willNotify', 'We\'ll notify you when something important happens')}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {notifications.map((notification) => (
                  <NotificationItem 
                    key={notification.id} 
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    isUnread={isUnread(notification)}
                  />
                ))}
              </div>
              
              {hasMore && (
                <div className="mt-6 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 inline-flex items-center"
                  >
                    {loading ? (
                      <>
                        <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.loading', 'Loading...')}
                      </>
                    ) : (
                      <>
                        <ChevronDownIcon className="w-4 h-4 mr-2" />
                        {t('notifications.loadMore', 'Load more')}
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications; 